﻿namespace Validation

module Ensure =

    open System

      ////////////
     // README //
    ////////////
    //
    // Guidelines for adding to this library.
    // 1. Functions must be chainable.
    //    The data-to-be-validated must be last in parameter order.
    // 2. Functions should be single purpose.
    //    Multi level ifs/matches are a code smell.
    //    Instead, compose from other validations
    //    and place them in Messages.Validators.
    // 3. The error should be the first parameter.
    //    Otherwise it is easy to forget the error tag.
    //    Especially with functions that take a fn parameter, 
    //    the compiler error is hard to figure out.

    let untrue err f x =
        if f x then
            Error (List.singleton err)
        else
            Ok x


    let istrue err f x =
        if not (f x) then
            Error (List.singleton err)
        else
            Ok x


    let nonBlank err (s: string) =
        if String.IsNullOrWhiteSpace s then
            Error (List.singleton err)
        else
            Ok s


    let nonBlankOpt err (s: string) =
        match nonBlank err s with
        | Error err ->
            Error err
        | Ok s ->
            Ok (Some s)


    let nonEmptyGuid err (g: Guid) =
        if g = Guid.Empty then
            Error (List.singleton err)
        else
            Ok g


    let nonEmptyList err (l: _ list) =
        match l with
        | [] ->
            Error (List.singleton err)
        | _ ->
            Ok l


    let blankAsNone (s: string) =
        if String.IsNullOrWhiteSpace s then
            Ok None
        else
            Ok (Some s)


    let listOf f (l: _ list) =
        let init = [], []
        let update (oks, errors) item =
            match f item with
            | Ok x ->
                oks @ [x], errors
            | Error errs ->
                oks, errors @ errs
        let oks, errors = List.fold update init l
        match errors with
        | [] -> Ok oks
        | _ -> Error errors


    let nonEmptyListOf f err (l: _ list) =
        match nonEmptyList err l with
        | Error err ->
            Error err
        | _ ->
            listOf f l


    let isNone err x =
        match x with
        | None ->
            Ok ()
        | Some a ->
            Error (List.singleton (err a))


    let minLength err len (text: string) =
        if text.Length < len then
            Error [err len]
        else
            Ok text


    let maxLength err i (s: string) =
        if s.Length > i then
            Error (List.singleton (err i))
        else
            Ok s


    let toDecimal err (s: string) =
        match Decimal.TryParse s with
        | false, _ ->
            Error (List.singleton err)
        | true, d ->
            Ok d


    let maxDecimalPlaces err i (d : Decimal) =
        let decimalOnly = d - (Math.Truncate d)
        let scaled = decimalOnly * (pown 10m i)
        if Math.Truncate scaled <> scaled then
            Error (List.singleton err)
        else
            Ok d


    let toInt err (s: string) =
        match Int32.TryParse s with
        | false, _ ->
            Error (List.singleton err)
        | true, i ->
            Ok i


    let isInRange err min max x =
        if min <= x && x <= max then
            Ok x
        else
            Error (List.singleton err)


    // FIXME rename to atOrBelow
    let isBelow err max x =
        if x <= max then
            Ok x
        else
            Error (List.singleton err)


    // FIXME rename to atOrAbove
    let isAbove err min x =
        if x >= min then
            Ok x
        else
            Error (List.singleton err)


    let rec oneOf err list x =
        match list with
        | [] ->
            Error (List.singleton err)
        | item :: rest ->
            if item = x then
                Ok x
            else
                oneOf err rest x


    let allOf ferrs x =
        List.fold (fun state f ->
            match state, f x with
            | Ok _, Ok _ ->
                Ok x
            | Ok _, Error errs ->
                Error errs
            | Error errf, Ok _ ->
                Error errf
            | Error errf, Error errs ->
                Error (List.append errf errs)
        ) (Ok x) ferrs


    let noneOr f x =
        match x with
        | None ->
            Ok None
        | Some v ->
            f v
            |> Result.map Some

    //Old regex 9/1/2022 - ^[\w&^*%#~{}=+?`-]+(?:\.[\w&^*%#~{}=+?`-]+)*@(?:[a-zA-Z0-9]+\.)+[a-zA-Z]{2,}$
    //Does not accept hyphen after @ symbol

    open System.Text.RegularExpressions
    let emailRegex = new Regex(@"^[\w&^*%#~{}=+?`-]+(?:\.[\w&^*%#~{}=+?`-]+)*@(?:[\w&^*%#~{}=+?`-]+\.)+[a-zA-Z]{2,}$", RegexOptions.IgnoreCase)
    let isEmailLike err x =
        if emailRegex.IsMatch(x) then
            Ok x
        else
            Error (List.singleton err)


    let toGuid err (s: string) =
        match Guid.TryParse (s.Trim()) with
        | false, _ ->
            Error (List.singleton err)
        | true, i ->
            Ok i


    let validDate err (x: string) =
        match System.DateTime.TryParse x with
        | false, _ ->
            Error (List.singleton err)
        | true, d ->
            Ok d


    let validDateString err (x: string) =
        match System.DateTime.TryParse x with
        | false, _ ->
            Error (List.singleton err)
        | true, _ ->
            Ok x


      ///////////
     // FIXME //
    ///////////
    // the stuff below here does not belong
    // This library is for generic composable validations
    // These should be moved to Messages.Validators

    // FIXME this is redundant with:
    //   Ensure.isAbove err min x
    let isTodayOrAfter (today: DateTime) err date =
        if date >= today then
            Ok date
        else
            Error (List.singleton err)


    // FIXME this can be composed from other validations
    // FIXME this parameter order makes it unchainable
    //       s should be last
    let expirationDate err (s: string) (currentDate: DateTime) =
        // point-free style
        //String.trimmed
        //>> minLength (fun _ -> err) 5
        //>=> validDate err
        //>=> isBelow err currentDate
        //>=> isAbove err (currentDate.AddYears(5))
        // more readable style
        //Ok (String.trimmed s)
        //|> andThen (minLength (fun _ -> err) 5)
        //|> andThen (validDate err)
        //|> andThen (isBelow err currentDate)
        //|> andThen (isAbove err (currentDate.AddYears(5)))

        if s.Length < 5 then
            Error (List.singleton err)
        else
            match System.DateTime.TryParse s with
            | false, _ ->
                Error (List.singleton err)
            | true, d ->
                if currentDate > d then
                    Error (List.singleton err)
                else
                    if d > currentDate.AddYears(5) then
                        Error (List.singleton err)
                    else
                        Ok s

    // FIXME this can be composed from other validations
    // FIXME this parameter order makes it unchainable
    //       s should be last
    let expirationDateTen err (s: string) (currentDate: DateTime) =
        if s.Length < 10 then
            Error (List.singleton err)
        else
            match System.DateTime.TryParse s with
            | false, _ ->
                Error (List.singleton err)
            | true, d ->
                if currentDate > d then
                    Error (List.singleton err)
                else
                    if d > currentDate.AddYears(10) then
                        Error (List.singleton err)
                    else
                        Ok s

    // FIXME this can be composed from other validations
    let expirationDateCheck err (s: string) = //only checks if the entered data is a valid date
        if s.Length < 10 then
            Error (List.singleton err)
        else
            match System.DateTime.TryParse s with
            | false, _ ->
                Error (List.singleton err)
            | true, d ->
                Ok s